import type { CCPreset } from '~/types/presets';
import type { MidiCC, DeviceProfile } from '~/types/midi';

/**
 * Default CC preset for YO! FM Synth
 *
 * This preset provides a carefully tuned set of MIDI CC parameters
 * optimized for FM synthesis. The CC values are designed to produce
 * rich, evolving timbres when scrambled.
 *
 * Key characteristics:
 * - Moderate harmonicity (CC14: 104) allows for both harmonic and inharmonic sounds
 * - High modulation index (CC15: 110) creates complex timbres
 * - Envelope settings favor punchy attacks with sustained releases
 * - Filter cutoff and resonance add movement and character
 */

const defaultFMSynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 80, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 80, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 14, value: 79, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 15, value: 80, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 16, value: 16, scramble: true, smooth: false, show: true, min: 0, max: 100 },
  { number: 17, value: 100, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 18, value: 108, scramble: true, smooth: false, show: true, min: 20, max: 127 },
  { number: 19, value: 120, scramble: true, smooth: false, show: true, min: 5, max: 127 },
  { number: 70, value: 38, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 72, value: 39, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 73, value: 57, scramble: true, smooth: false, show: true, min: 3, max: 80 },
  { number: 74, value: 101, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 75, value: 31, scramble: true, smooth: false, show: true, min: 10, max: 127 },
  { number: 76, value: 7, scramble: true, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * Default device profile for YO! FM Synth
 *
 * This profile enables scrambling for FM-specific parameters with
 * safe min/max ranges to ensure musical results.
 */
const defaultFMSynthProfile: DeviceProfile = {
  id: 'yo-fm-synth-default',
  name: 'Default',
  deviceName: 'YO! FM Synth',
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    // === STANDARD MIDI CCs ===
    {
      number: 1,
      enabled: false, // Disabled by default - can be disruptive
      min: 0,
      max: 127,
      label: 'Modulation',
    },
    {
      number: 7,
      enabled: false, // Disabled - dangerous if too low
      min: 80, // ~-10dB - loud and clear
      max: 110, // ~-2dB - very loud but not clipping
      label: 'Volume',
    },
    {
      number: 11,
      enabled: false, // Disabled - same as volume
      min: 80, // ~-10dB - loud and clear
      max: 110, // ~-2dB - very loud but not clipping
      label: 'Expression',
    },

    // === FM SYNTHESIS PARAMETERS ===
    {
      number: 14,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulation Index',
    },
    {
      number: 15,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Harmonicity',
    },

    // === MODULATION ENVELOPE ===
    {
      number: 16,
      enabled: false, // Disabled - mod env attack kept stable for consistent timbre evolution
      min: 0,
      max: 100,
      label: 'Mod Env Attack',
    },
    {
      number: 17,
      enabled: true, // ✅ Mod Env Decay - safe to scramble
      min: 5,
      max: 127,
      label: 'Mod Env Decay',
    },
    {
      number: 18,
      enabled: true, // ✅ Mod Env Sustain - safe to scramble, affects brightness
      min: 20,
      max: 127,
      label: 'Mod Env Sustain',
    },
    {
      number: 19,
      enabled: true, // ✅ Mod Env Release - safe to scramble (less critical)
      min: 5,
      max: 127,
      label: 'Mod Env Release',
    },

    // === CARRIER ENVELOPE ===
    {
      number: 70,
      enabled: true, // ✅ Carrier Sustain - safe to scramble
      min: 0,
      max: 127,
      label: 'Carrier Sustain',
    },
    {
      number: 72,
      enabled: false, // Disabled - too short causes abrupt cutoff, keep preset value
      min: 10,
      max: 127,
      label: 'Carrier Release',
    },
    {
      number: 73,
      enabled: false, // Disabled - too short causes clicks, keep preset value
      min: 3,
      max: 80,
      label: 'Carrier Attack',
    },
    {
      number: 74,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulator Type',
    },
    {
      number: 75,
      enabled: true,
      min: 10,
      max: 127,
      label: 'Carrier Decay',
    },
    {
      number: 76,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Carrier Type',
    },
  ],
};

/**
 * Default FM Synth CC Preset
 */
export const DEFAULT_FM_SYNTH_PRESET: CCPreset = {
  id: 'default-fm',
  name: 'Default FM Synth',
  deviceType: 'FMSynth',
  cc: defaultFMSynthCCs,
  profiles: [defaultFMSynthProfile],
  activeProfileId: 'yo-fm-synth-default',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the default FM Synth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getDefaultFMSynthPreset(): CCPreset {
  return {
    ...DEFAULT_FM_SYNTH_PRESET,
    cc: DEFAULT_FM_SYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: DEFAULT_FM_SYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
